import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiPost, operationsClient } from '@/lib/api';
import { timesheetEndpoints } from '@/lib/api/endpoints';
import { normalizeError } from '@/lib/api/errors';
import { timesheetKeys } from '@/lib/queryKeys';
import type { ApproveTimesheetDto, TimesheetApprovalState } from '@/types/timesheet';

/**
 * Approve several technicians' periods in one action.
 *
 * Runs them one at a time and keeps going past a refusal. The per-row hook
 * toasts and invalidates on every call, which for a team of twenty is twenty
 * toasts and twenty refetches — and awaiting it in a loop stopped the whole
 * batch at the first technician the backend refused, leaving the rest
 * unapproved with no word about which.
 */
export function useApproveTimesheets() {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);

  async function approveAll(items: Array<ApproveTimesheetDto & { technicianName: string }>) {
    setIsPending(true);
    const failed: Array<{ name: string; message: string }> = [];
    try {
      for (const { technicianName, ...dto } of items) {
        try {
          await apiPost<TimesheetApprovalState, ApproveTimesheetDto>(
            operationsClient,
            timesheetEndpoints.approve(),
            dto,
          );
        } catch (err) {
          failed.push({ name: technicianName, message: normalizeError(err).message });
        }
      }
    } finally {
      await queryClient.invalidateQueries({ queryKey: timesheetKeys.periods() });
      setIsPending(false);
    }

    const approved = items.length - failed.length;
    if (approved > 0) {
      toast.success(`${approved} ${approved === 1 ? 'timesheet' : 'timesheets'} approved`);
    }
    if (failed.length > 0) {
      toast.error(`Couldn't approve ${failed.map((f) => f.name).join(', ')}`, {
        description: failed[0].message,
      });
    }
    return { approved, failed };
  }

  return { approveAll, isPending };
}
